import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { useEmbed } from "@/contexts/EmbedContext";
import { t } from "@/i18n";
import { ShieldCheck, Info, X, Check, Link2, Download, Unlink } from "lucide-react";
import { resolveConnectorBadge } from "./privacyVaultBadge";
import { formatFetchedAt, formatPeriodNL, formatLongDateNL, formatIntNL, formatCtrPctNL, formatPositionNL, pluralizeNL } from "@/utils/formatFetchedAt";

const LEVELS = ["PRIVATE", "READ", "ADVISE", "ACT"];

// M127-fix (outer-badge) — Google-connector-kaarten lezen hun buitenste
// "Verbonden / Niet gekoppeld"-badge uit de centrale bron
// `GET /api/auth/google/services/status` via `resolveConnectorBadge`
// (zie `./privacyVaultBadge.js`). Niet-Google-bronnen blijven de oude
// `db.sources.connected`-fallback volgen.

export default function PrivacyVaultPage() {
  const { hideInnerHeader } = useEmbed();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [gSvc, setGSvc] = useState({});  // M127-fix: services-status uit centrale bron
                                          //   voor Google-connector-badges.

  const load = async () => {
    try {
      const { data } = await api.get("/privacy/vault");
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || t("common.error_generic"));
    }
    try {
      const { data: gs } = await api.get("/auth/google/services/status");
      setGSvc(gs?.services || {});
    } catch (_e) {
      // stil — Google-badges vallen terug op /vault-status (uitzondering)
    }
  };
  useEffect(() => { load(); }, []);

  const setPermission = async (key, level) => {
    try {
      await api.patch(`/privacy/vault/${key}/permission`, { permission: level });
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || t("common.error_generic"));
    }
  };

  if (!data) return <AppLayout><div className="text-text-muted">{t("common.loading")}</div></AppLayout>;

  return (
    <AppLayout>
      {!hideInnerHeader && (
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="w-6 h-6 text-accent-teal" />
            <h1 className="text-2xl font-bold text-text-primary tracking-tight" data-testid="vault-title">
              {t("vault.title")}
            </h1>
          </div>
          <p className="text-sm text-text-muted max-w-3xl">{t("vault.subtitle")}</p>
        </div>
      )}

      <div className="mb-8 flex gap-3 p-4 rounded-xl border border-info-blue/40 bg-info-blue/10">
        <Info className="w-5 h-5 text-accent-teal mt-0.5 shrink-0" />
        <p className="text-sm text-info-blue leading-relaxed">{data.notice_nl}</p>
      </div>

      {error && (
        <div className="mb-6 p-3 rounded-md border border-neg-red/40 bg-neg-red/10 text-neg-red text-sm">{error}</div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mb-10">
        {data.connectors.map((c) => (
          <ConnectorCard key={c.key} connector={c} gSvc={gSvc} onSet={(l) => setPermission(c.key, l)} />
        ))}
      </div>

      <div className="rounded-xl border border-border-hairline bg-bg-panel p-6">
        <h3 className="text-sm text-text-muted font-semibold mb-4">
          {t("vault.never_collects")}
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {data.never_collects.map((item) => (
            <div key={item} className="flex items-center gap-2 text-sm text-text-primary/85">
              <X className="w-3.5 h-3.5 text-red-500 shrink-0" />
              {item}
            </div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}

function GoogleAdsWriteScopeBadge() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/privacy/sources/google_ads/scope-status");
        if (!cancelled) setStatus(r.data);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);
  if (!status || !status.connected) return null;

  // M134-correctie — 3-laag capability-check. M134-C5: "aangetoond door
  // recente import" is een geldige scope-bron (Google Ads heeft één scope).
  const scopeSrc = status.google_scope_source;
  const scopeLabel = (status.google_scope_ok && scopeSrc === "recent_read_import")
    ? "Aangetoond via recente import"
    : (status.google_scope_ok ? "OK" : "Niet ok");
  const layers = [
    { key: "scope", label: "Google-scope",
      ok: status.google_scope_ok,
      value_label: scopeLabel,
      hint_bad: "Scope ontbreekt. Opnieuw verbinden in Instellingen." },
    { key: "role", label: "Account-rol",
      ok: status.account_role_ok,
      pending: status.account_role === "unknown",
      hint_bad: (status.account_role === "unknown"
        ? "Nog niet gecontroleerd"
        : `Rol: ${status.account_role}`) },
    { key: "perm", label: "NEXORA-permissie",
      ok: status.nexora_permission_execute_ok,
      validate_ok: status.nexora_permission_validate_ok,
      hint_bad: (status.nexora_permission_validate_ok
        ? `Nu op ${status.nexora_permission} — alleen valideren`
        : `Nu op ${status.nexora_permission} — te laag`) },
  ];
  const summary_ok = status.can_execute_mutations;
  const summary_validate_only = (!summary_ok
    && status.google_scope_ok
    && status.nexora_permission_validate_ok);

  return (
    <div className="mt-3 rounded-md border border-border-hairline bg-bg-panel-raised p-3 space-y-2"
          data-testid="ads-write-capability">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-text-primary font-semibold">
          Mutatie-capaciteit (Google Ads)
        </div>
        {summary_ok ? (
          <span className="text-[10px] px-2 py-0.5 rounded border border-pos-green/40 bg-pos-green/10 text-pos-green font-semibold"
                 data-testid="ads-write-summary-active">
            Schrijfrechten actief
          </span>
        ) : summary_validate_only ? (
          <span className="text-[10px] px-2 py-0.5 rounded border border-info-blue/40 bg-info-blue/10 text-info-blue font-semibold"
                 data-testid="ads-write-summary-validate-only">
            Alleen valideren
          </span>
        ) : (
          <span className="text-[10px] px-2 py-0.5 rounded border border-warn-amber/40 bg-warn-amber/10 text-warn-amber font-semibold"
                 data-testid="ads-write-summary-blocked">
            Schrijven nog niet mogelijk
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {layers.map((L) => (
          <div key={L.key}
                data-testid={`ads-capability-layer-${L.key}`}
                className={`rounded border p-2 text-[10px] ${L.ok ? "border-pos-green/40 bg-pos-green/5" : L.pending ? "border-border-hairline bg-bg-panel" : "border-warn-amber/40 bg-warn-amber/5"}`}>
            <div className="text-text-muted mb-0.5">
              {L.label}
            </div>
            <div className={`font-semibold ${L.ok ? "text-pos-green" : L.pending ? "text-text-muted" : "text-warn-amber"}`}>
              {L.ok ? (L.value_label || "OK") : L.pending ? "Nog niet gecontroleerd" : "Niet ok"}
            </div>
            {!L.ok && (
              <div className="text-text-muted italic mt-0.5">{L.hint_bad}</div>
            )}
          </div>
        ))}
      </div>
      <div className="text-[11px] text-text-muted leading-relaxed"
            data-testid="ads-write-hint">
        {status.hint_nl}
      </div>
      {/* Alleen bij hard scope=absent (niet bij recent_read_import) tonen we een subtiele Beheren-link. */}
      {!status.google_scope_ok && scopeSrc === "absent" && (
        <a href="/kanalen"
            data-testid="ads-write-manage-link"
            className="inline-block text-[11px] px-2 py-1 rounded border border-accent-teal/40 text-accent-teal hover:bg-accent-teal/10">
          Beheren in Kanalen
        </a>
      )}
    </div>
  );
}

function ConnectorCard({ connector, gSvc, onSet }) {
  const disabled = !connector.available;
  const isGoogleAds = connector.key === "google_ads";
  const badge = resolveConnectorBadge(connector, gSvc);
  return (
    <div
      data-testid={`connector-card-${connector.key}`}
      className={`rounded-xl border ${disabled ? "border-border-hairline bg-bg-panel" : "border-border-hairline bg-bg-panel"} p-5`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-semibold text-text-primary">{connector.name}</div>
          <div className="flex items-center gap-2 text-xs mt-1 tracking-wider">
            {badge.verified ? (
              <span className="text-pos-green font-semibold"
                    data-testid={`connector-outer-badge-${connector.key}`}>
                {badge.label}
              </span>
            ) : (
              <span className="text-text-muted"
                    data-testid={`connector-outer-badge-${connector.key}`}>
                {badge.label}
              </span>
            )}
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-info-blue/10 text-info-blue border border-info-blue/40"
                  title={t("vault.read_only_hint")}>
              {t("vault.read_only_pill")}
            </span>
            {connector.authority_tier && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-bg-panel-raised text-text-muted border border-border-hairline"
                    title={t(`enum.tier_explain.${connector.authority_tier}`)}>
                {t(`enum.tier.${connector.authority_tier}`)}
              </span>
            )}
          </div>
        </div>
        {disabled && (
          <span className="text-[9px] font-bold px-2 py-1 rounded bg-bg-panel-raised text-text-muted border border-border-hairline">
            {t("vault.not_available")}
          </span>
        )}
      </div>

      <p className="text-xs text-text-muted mb-4 leading-relaxed">{connector.why}</p>
      {isGoogleAds && <GoogleAdsWriteScopeBadge />}

      <div className="flex gap-1 mb-4" role="radiogroup">
        {LEVELS.map((l) => {
          const active = connector.permission === l;
          const isAct = l === "ACT";
          const btnDisabled = disabled || isAct;
          return (
            <button
              key={l}
              onClick={() => !btnDisabled && onSet(l)}
              disabled={btnDisabled}
              data-testid={`permission-pill-${connector.key}-${l}`}
              title={isAct ? t("vault.act_disabled") : t(`vault.permission_desc.${l}`)}
              className={`flex-1 h-8 rounded-md text-[10px] font-bold transition-colors ${ active ? l === "PRIVATE" ? "bg-neutral-800 text-white border border-neutral-800" : l === "READ" ? "bg-info-blue/20 text-info-blue border border-info-blue/50" : l === "ADVISE" ? "bg-purple-100 text-purple-800 border border-purple-300" : "bg-pos-green/20 text-pos-green border border-pos-green/50" : "bg-bg-panel text-text-muted border border-border-hairline hover:border-accent-teal/40 hover:text-text-primary/85" } ${btnDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              {t(`vault.permission_labels.${l}`)}
            </button>
          );
        })}
      </div>

      {isGoogleAds && <GoogleAdsBox connector={connector} onChanged={() => window.location.reload()} />}
      {connector.key === "ga4" && <GoogleAnalyticsBox connector={connector} />}
      {connector.key === "gsc" && <GoogleSearchConsoleBox connector={connector} />}

      <details className="group">
        <summary
          data-testid={`connector-details-toggle-${connector.key}`}
          className="cursor-pointer text-xs text-text-muted hover:text-text-primary select-none list-none flex items-center gap-1 font-medium"
        >
          <span className="group-open:rotate-90 transition-transform inline-block">▶</span> Details
        </summary>
        <div className="mt-3 space-y-3 pl-4 border-l-2 border-border-hairline">
          <div>
            <div className="text-[10px] text-pos-green font-semibold mb-1.5">
              {t("vault.can_see")}
            </div>
            <ul className="space-y-1">
              {connector.can_see.map((c) => (
                <li key={c} className="flex items-start gap-2 text-xs text-text-primary/85">
                  <Check className="w-3 h-3 mt-0.5 text-pos-green shrink-0" /> {c}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[10px] text-neg-red font-semibold mb-1.5">
              {t("vault.cannot_see")}
            </div>
            <ul className="space-y-1">
              {connector.cannot_see.map((c) => (
                <li key={c} className="flex items-start gap-2 text-xs text-text-primary/85">
                  <X className="w-3 h-3 mt-0.5 text-red-500 shrink-0" /> {c}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </details>
    </div>
  );
}

// ---------------------------------------------------------------------------
// GoogleAdsBox — inline Google Ads connect/ingest/disconnect UI.
// Uses backend endpoints from routers/integrations_google_ads.py.
// Rendered only inside the Google Ads ConnectorCard when permission != PRIVATE.
// ---------------------------------------------------------------------------
// Google Ads privacy-kaart — M127: leest uitsluitend uit de centrale bron
// `GET /api/auth/google/services/status`. Geen `/integrations/google-ads/*`
// meer, geen invoerveld, geen testaccount-widget, geen ingest-knop. De
// koppeling zelf gebeurt op /instellingen/verbindingen — hier alleen
// weergave + "Beheren"-link naar die centrale pagina.
// Export voor render-smoke-tests (M128 G1/G2).
// ---------------------------------------------------------------------------
export function GoogleAdsBox({ connector }) {
  const [svc, setSvc] = useState(null);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importErr, setImportErr] = useState(null);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/auth/google/services/status");
      setSvc((data?.services || {}).ads || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Kon status niet ophalen.");
    }
  };
  const loadReport = async () => {
    try {
      const { data } = await api.get("/google/ads/report");
      setReport(data);
    } catch (_e) {
      setReport(null);  // 404 = nog geen rapport
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) { await loadStatus(); await loadReport(); } })();
    return () => { cancelled = true; };
  }, []);

  const doImport = async () => {
    setImporting(true); setImportErr(null);
    try {
      await api.post("/google/ads/import", { lookback_days: 30 });
      // M128 G4 — advies-hergeneratie zit nu server-side in
      // `run_ads_import` zelf; geen frontend-call meer nodig.
      await loadReport();
    } catch (e) {
      const d = e?.response?.data?.detail || {};
      setImportErr(d?.reason_nl || "Ophalen mislukt. Probeer opnieuw.");
    } finally {
      setImporting(false);
    }
  };

  const disabled = connector.permission === "PRIVATE";
  const isVerified = svc?.state === "verified";
  const stateLabelNL = svc?.last_status_nl
    || svc?.state_nl
    || (svc ? "Wordt opgehaald" : "Nog niet gekoppeld");
  const t = report?.totals || null;
  const cur = report?.currency_code || "EUR";
  const fmtNum = (n) => Number(n || 0).toLocaleString("nl-NL");
  const fmtMoney = (n) => `${cur === "EUR" ? "€ " : cur + " "}` +
    Number(n || 0).toLocaleString("nl-NL",
      { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtWhen = (iso) => formatFetchedAt(iso, report?.time_zone);

  return (
    <div id="google-ads"
         data-testid="google-ads-box"
         className="mb-4 p-3 rounded-md border border-border-hairline bg-bg-panel space-y-2 scroll-mt-24">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-text-primary font-semibold">
          Google Ads
        </div>
        <div className="text-[10px]">
          {isVerified
            ? <span className="text-pos-green font-semibold"
                     data-testid="google-ads-verified-badge">Verbonden</span>
            : <span className="text-text-subtle"
                     data-testid="google-ads-not-verified-badge">Niet verbonden</span>}
        </div>
      </div>

      <div className="text-[11px] text-text-muted leading-relaxed"
           data-testid="google-ads-state-nl">
        {isVerified
          ? `Verbonden met ${svc?.account_name || "Google Ads-account"}`
          : stateLabelNL}
      </div>
      <SchedulerStatusLine svc={svc} testid="google-ads-scheduler-line" />

      <div className="text-[11px] text-text-muted leading-relaxed">
        NEXORA leest alleen kosten, klikken, vertoningen en conversies per
        campagne. Geen wijzigingen.
      </div>

      {/* M128 — prestaties */}
      {isVerified && t && (
        <div data-testid="google-ads-performance"
             className="rounded border border-border-hairline bg-bg-panel-raised p-3 space-y-2">
          <div className="text-[10px] text-text-muted">
            Prestaties {formatPeriodNL(report.period_start, report.period_end)}
          </div>
          {report.time_zone && (
            <div className="text-[9px] text-text-subtle -mt-1">
              tijdzone: {report.time_zone}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Kpi label="Advertentiekosten"
                 value={fmtMoney(t.cost)}
                 testid="ads-kpi-cost" />
            <Kpi label="Klikken"
                 value={fmtNum(t.clicks)}
                 testid="ads-kpi-clicks" />
            <Kpi label="Vertoningen"
                 value={fmtNum(t.impressions)}
                 testid="ads-kpi-impressions" />
            <Kpi label="Gemeten conversies"
                 value={fmtNum(t.conversions)}
                 sub="gemeten door Google Ads, niet gelijk aan klanten"
                 testid="ads-kpi-conversions" />
          </div>
          {report.is_empty_success && (
            <div className="text-[11px] text-text-muted"
                 data-testid="ads-empty-period">
              Geen advertentiegegevens in deze periode.
            </div>
          )}
          {report.last_error_status && (
            <div className="text-[11px] text-warn-amber"
                 data-testid="ads-last-error">
              Laatste update mislukt op {fmtWhen(report.last_error_at)} —
              {" "}{report.last_error_reason_nl}
            </div>
          )}
          <div className="text-[10px] text-text-subtle">
            Laatst bijgewerkt op {fmtWhen(report.fetched_at)}
          </div>
        </div>
      )}
      {isVerified && !t && (
        <div className="text-[11px] text-text-muted"
             data-testid="google-ads-no-data-yet">
          Nog geen gegevens opgehaald.
        </div>
      )}

      {err && (
        <div className="text-[11px] text-neg-red">{err}</div>
      )}
      {importErr && (
        <div className="text-[11px] text-neg-red"
             data-testid="ads-import-error">{importErr}</div>
      )}

      {!disabled && (
        <div className="flex gap-2">
          {isVerified && (
            <button type="button"
                    onClick={doImport}
                    disabled={importing}
                    data-testid="google-ads-refresh-btn"
                    className="h-8 px-3 rounded bg-accent-teal/15 hover:bg-accent-teal/25 text-accent-teal border border-accent-teal/40 text-xs font-semibold flex items-center gap-1 disabled:opacity-60">
              <Download className="w-3 h-3" />
              {importing ? "Bezig met ophalen…" : (t ? "Bijwerken" : "Gegevens ophalen")}
            </button>
          )}
          <a href="/kanalen"
             data-testid="google-ads-manage-link"
             className="h-8 px-3 rounded bg-bg-panel hover:bg-bg-panel-raised text-text-primary/85 border border-border-hairline text-xs font-semibold flex items-center gap-1">
            <Link2 className="w-3 h-3" /> Beheren
          </a>
        </div>
      )}

      {disabled && (
        <div className="text-[11px] text-text-muted">
          Zet permissie op LEZEN of hoger om te kunnen koppelen.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// GoogleSearchConsoleBox — M128 Fase G3. Read-only status + rapport-view voor
// Google Search Console; endpoint `/google/gsc/*`. Fetched-at wordt getoond
// in de provider-tijdzone (America/Los_Angeles) omdat GSC-rapporten in PT
// worden verankerd. CTR en positie worden getoond op siteniveau (bron: de
// aparte dimensieloze call); per-rij CTR/positie worden bewust WEL bewaard
// maar in de UI verbergen we ze om verwarrende "gemiddelden" te voorkomen.
// Named-export voor render-smoke-test.
// ---------------------------------------------------------------------------
export function GoogleSearchConsoleBox({ connector }) {
  const [svc, setSvc] = useState(null);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importErr, setImportErr] = useState(null);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/auth/google/services/status");
      setSvc((data?.services || {}).gsc || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Kon status niet ophalen.");
    }
  };
  const loadReport = async () => {
    try {
      const { data } = await api.get("/google/gsc/report");
      setReport(data);
    } catch (_e) {
      setReport(null);
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) { await loadStatus(); await loadReport(); } })();
    return () => { cancelled = true; };
  }, []);

  const doImport = async () => {
    setImporting(true); setImportErr(null);
    try {
      await api.post("/google/gsc/import", { lookback_days: 28 });
      // M128 G4 — advies-hergeneratie zit nu server-side in
      // `run_gsc_import` zelf.
      await loadStatus();
      await loadReport();
    } catch (e) {
      const d = e?.response?.data?.detail || {};
      setImportErr(d?.reason_nl
        || "Ophalen is nu niet mogelijk. Probeer het later opnieuw.");
    } finally {
      setImporting(false);
    }
  };

  const askPlatformFix = async () => {
    setImporting(true); setImportErr(null);
    try {
      await api.post("/auth/google/services/platform-action", {
        service: "gsc",
        reason_raw: svc?.last_reason_raw
          || report?.last_error_reason_raw || null,
      });
      setImportErr("Herstelverzoek genoteerd bij NEXORA.");
    } catch (_e) {
      setImportErr("Kon verzoek niet vastleggen. Probeer later opnieuw.");
    } finally {
      setImporting(false);
    }
  };

  const disabled = connector.permission === "PRIVATE";
  const isVerified = svc?.state === "verified";
  const stateLabelNL = svc?.last_status_nl
    || svc?.state_nl
    || (svc ? "Wordt opgehaald" : "Nog niet gekoppeld");
  const t = report?.totals || null;
  const meta = report?.meta || {};
  const providerTz = meta.provider_time_zone || "America/Los_Angeles";
  // M128 G3-afwerking (fix): `fetched_at` = bijwerktijd, hoort in de
  // bedrijfstijdzone (Europe/Amsterdam) — NIET in provider-tijdzone.
  // Provider-tijdzone (PT) blijft de bron voor de PERIODE en
  // `last_date_with_data`, die zijn kalenderdagen uit de GSC-response.
  const fmtNum = (n) => formatIntNL(n);
  const fmtPct = (n) => n == null ? "onbekend" : formatCtrPctNL(n);
  const fmtPos = (n) => n == null ? "onbekend" : formatPositionNL(n);
  const fmtWhen = (iso) => formatFetchedAt(iso, "Europe/Amsterdam");
  const daysWithData = meta.days_with_data;
  const lookback = meta.lookback_days || 28;

  return (
    <div id="google-search-console"
         data-testid="google-search-console-box"
         className="mb-4 p-3 rounded-md border border-border-hairline bg-bg-panel space-y-2 scroll-mt-24">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-text-primary font-semibold">
          Google Search Console
        </div>
        <div className="text-[10px]">
          {isVerified
            ? <span className="text-pos-green font-semibold"
                     data-testid="google-search-console-verified-badge">Verbonden</span>
            : <span className="text-text-subtle"
                     data-testid="google-search-console-not-verified-badge">Niet verbonden</span>}
        </div>
      </div>

      <div className="text-[11px] text-text-muted leading-relaxed"
           data-testid="google-search-console-state-nl">
        {isVerified
          ? `Verbonden met ${svc?.account_name || "Search Console-website"}`
          : stateLabelNL}
      </div>
      <SchedulerStatusLine svc={svc} testid="google-search-console-scheduler-line" />

      <div className="text-[11px] text-text-muted leading-relaxed">
        NEXORA leest alleen zoekvertoningen, kliks en gemiddelde positie
        van uw website in Google Zoeken. Geen wijzigingen.
      </div>

      {isVerified && t && (
        <div data-testid="google-search-console-performance"
             className="rounded border border-border-hairline bg-bg-panel-raised p-3 space-y-2">
          <div className="text-[10px] text-text-muted">
            Zichtbaarheid {formatPeriodNL(report.period_start, report.period_end)}
          </div>
          <div className="text-[9px] text-text-subtle -mt-1">
            provider-tijdzone: {providerTz}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Kpi label="Klikken vanuit Google"
                 value={fmtNum(t.clicks)}
                 testid="gsc-kpi-clicks" />
            <Kpi label="Vertoningen"
                 value={fmtNum(t.impressions)}
                 testid="gsc-kpi-impressions" />
            <Kpi label="Klikratio"
                 value={fmtPct(t.ctr)}
                 sub="site-brede waarde uit Google"
                 testid="gsc-kpi-ctr" />
            <Kpi label="Gem. positie"
                 value={fmtPos(t.position)}
                 sub="lager = beter"
                 testid="gsc-kpi-position" />
          </div>

          {/* Coverage-label — eerlijk over wat GSC leverde */}
          {daysWithData != null && (
            <div className="text-[10px] text-text-subtle leading-snug"
                 data-testid="gsc-coverage-line">
              Google levert gegevens voor {daysWithData} dagen. Laatste datum met gegevens: {formatLongDateNL(meta.last_date_with_data)}.
              {daysWithData < lookback && (
                <div data-testid="gsc-coverage-partial">
                  Voor sommige dagen leverde Google geen zichtbare gegevens; recente dagen kunnen later volgen.
                </div>
              )}
            </div>
          )}

          {/* Top-lijsten — labels expliciet "belangrijkste zichtbare" */}
          {report.top_queries && report.top_queries.length > 0 && (
            <div className="pt-2 border-t border-border-hairline"
                 data-testid="gsc-top-queries">
              <div className="text-[9px] text-text-muted mb-1">
                Belangrijkste zichtbare zoekwoorden
              </div>
              <ul className="space-y-0.5">
                {report.top_queries.slice(0, 5).map((q) => (
                  <li key={q.query}
                      className="text-[11px] text-text-primary/85 flex justify-between gap-3">
                    <span className="truncate max-w-[60%]">{q.query}</span>
                    <span className="text-text-muted">
                      {pluralizeNL(q.clicks, "klik", "klikken")} · {pluralizeNL(q.impressions, "vertoning", "vertoningen")}
                    </span>
                  </li>
                ))}
              </ul>
              {meta.queries_capped && (
                <div className="text-[9px] text-text-subtle mt-1"
                     data-testid="gsc-queries-capped">
                  Maximaal 25 zoekwoorden opgehaald; alleen de belangrijkste staan hier.
                </div>
              )}
            </div>
          )}

          {report.top_pages && report.top_pages.length > 0 && (
            <div className="pt-2 border-t border-border-hairline"
                 data-testid="gsc-top-pages">
              <div className="text-[9px] text-text-muted mb-1">
                Belangrijkste zichtbare pagina's
              </div>
              <ul className="space-y-0.5">
                {report.top_pages.slice(0, 5).map((p) => (
                  <li key={p.page}
                      className="text-[11px] text-text-primary/85 flex justify-between gap-3">
                    <span className="truncate max-w-[60%]">{p.page}</span>
                    <span className="text-text-muted">
                      {pluralizeNL(p.clicks, "klik", "klikken")} · {pluralizeNL(p.impressions, "vertoning", "vertoningen")}
                    </span>
                  </li>
                ))}
              </ul>
              {meta.pages_capped && (
                <div className="text-[9px] text-text-subtle mt-1"
                     data-testid="gsc-pages-capped">
                  Maximaal 25 pagina's opgehaald; alleen de belangrijkste staan hier.
                </div>
              )}
            </div>
          )}

          {/* Lege of privacy-gefilterde top-lijsten */}
          {(!report.top_queries || report.top_queries.length === 0) && (
            <div className="text-[11px] text-text-muted"
                 data-testid="gsc-empty-queries">
              Beperkte gegevens beschikbaar voor zoekwoorden.
            </div>
          )}

          {report.is_empty_success && (
            <div className="text-[11px] text-text-muted"
                 data-testid="gsc-empty-period">
              Beperkte gegevens beschikbaar in deze periode.
            </div>
          )}
          {report.last_error_status && (
            <div className="text-[11px] text-warn-amber"
                 data-testid="gsc-last-error">
              Laatste update mislukt op {fmtWhen(report.last_error_at)} —
              {" "}{report.last_error_reason_nl}
            </div>
          )}
          <div className="text-[10px] text-text-subtle">
            Laatst bijgewerkt op {fmtWhen(report.fetched_at)}
          </div>
        </div>
      )}
      {isVerified && !t && !report?.last_error_status && (
        <div className="text-[11px] text-text-muted"
             data-testid="google-search-console-no-data-yet">
          Nog geen gegevens opgehaald.
        </div>
      )}
      {isVerified && report?.last_error_status && !t && (
        <div className="text-[11px] text-warn-amber leading-snug"
             data-testid="gsc-error-message">
          {report.last_error_reason_nl
            || "Ophalen is nu niet mogelijk."}
        </div>
      )}

      {err && (
        <div className="text-[11px] text-neg-red">{err}</div>
      )}
      {importErr && (
        <div className="text-[11px] text-neg-red"
             data-testid="gsc-import-error">{importErr}</div>
      )}

      {!disabled && isVerified && (
        <div className="flex flex-col gap-2">
          {(report?.last_error_status === "api_disabled"
            || report?.last_error_status === "provider_blocked") && (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[10px] text-text-muted max-w-[280px] leading-snug"
                   data-testid="gsc-platform-action-hint">
                Meldt intern dat NEXORA deze dienst moet herstellen. Google
                wordt hier niet automatisch geactiveerd.
              </div>
              <button type="button"
                      onClick={askPlatformFix}
                      disabled={importing}
                      data-testid="gsc-platform-action"
                      className="text-[11px] text-accent-teal hover:underline disabled:opacity-60 whitespace-nowrap">
                NEXORA laten herstellen
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <button type="button"
                    onClick={doImport}
                    disabled={importing}
                    data-testid="google-search-console-refresh-btn"
                    className="h-8 px-3 rounded bg-accent-teal/15 hover:bg-accent-teal/25 text-accent-teal border border-accent-teal/40 text-xs font-semibold flex items-center gap-1 disabled:opacity-60">
              <Download className="w-3 h-3" />
              {importing ? "Bezig met ophalen…" : (t ? "Bijwerken" : "Gegevens ophalen")}
            </button>
          </div>
        </div>
      )}

      {disabled && (
        <div className="text-[11px] text-text-muted">
          Zet permissie op LEZEN of hoger om te kunnen koppelen.
        </div>
      )}
    </div>
  );
}

import { SchedulerStatusLine } from "@/components/SchedulerStatusLine";

// M128 G5 — statusregel is nu geëxtraheerd naar `SchedulerStatusLine.jsx`
// zodat de render-tests niet via `AppLayout` -> `react-router-dom` gaan
// (pre-existing Jest-resolver-issue).

function Kpi({ label, value, sub, testid }) {  return (
    <div data-testid={testid}>
      <div className="text-[9px] text-text-muted">
        {label}
      </div>
      <div className="text-lg text-text-primary num">{value}</div>
      {sub && <div className="text-[9px] text-text-subtle leading-snug mt-0.5">{sub}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// M128 eindronde — GA4-foutlus-tekst per outcome. Provider-blokkades
// krijgen géén "later opnieuw"-zin (er is geen "later" waarop het vanzelf
// werkt zonder centrale activering / herkoppelen). Alleen echte
// temporary/unknown-fouten krijgen de retry-zin.
function _ga4ErrorTextForStatus(status, reasonNL) {
  const s = String(status || "").toLowerCase();
  if (s === "api_disabled" || s === "provider_blocked") {
    return reasonNL || "Deze dienst moet eerst door NEXORA worden geactiveerd.";
  }
  if (s === "grant_revoked") {
    return reasonNL || "Koppeling verlopen — opnieuw koppelen.";
  }
  if (s === "needs_consent") {
    return reasonNL || "Toestemming ontbreekt — koppel opnieuw.";
  }
  // temporary_error / unknown_error / netwerkfout → wél retry-zin.
  return reasonNL || "Ophalen is nu niet mogelijk. Probeer het later opnieuw.";
}

// GoogleAnalyticsBox — M128 Fase G2. Read-only status + rapport-view voor
// GA4. Volgt hetzelfde patroon als GoogleAdsBox; endpoint `/google/ga4/*`.
// Named-export voor render-smoke-test.
// ---------------------------------------------------------------------------
export function GoogleAnalyticsBox({ connector }) {
  const [svc, setSvc] = useState(null);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importErr, setImportErr] = useState(null);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/auth/google/services/status");
      setSvc((data?.services || {}).ga4 || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Kon status niet ophalen.");
    }
  };
  const loadReport = async () => {
    try {
      const { data } = await api.get("/google/ga4/report");
      setReport(data);
    } catch (_e) {
      setReport(null);
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) { await loadStatus(); await loadReport(); } })();
    return () => { cancelled = true; };
  }, []);

  const doImport = async () => {
    setImporting(true); setImportErr(null);
    try {
      const { data } = await api.post("/google/ga4/import", { lookback_days: 30 });
      // M128 eindronde — provider-blokkades komen nu als 200 met
      // gestructureerde payload terug (`ok:false, status:"api_disabled"`).
      // Selecteer klanttekst per outcome; nooit een generieke retry-zin
      // bij `api_disabled`/`grant_revoked`/`needs_consent`.
      if (data && data.ok === false) {
        setImportErr(_ga4ErrorTextForStatus(data.status, data.reason_nl));
      }
      // M128 G4 — advies-hergeneratie zit nu server-side in
      // `run_ga4_import` zelf.
      await loadStatus();
      await loadReport();
    } catch (e) {
      const d = e?.response?.data?.detail || {};
      // FastAPI raise-path — status uit `reason` binnen detail-body.
      const status = (typeof d === "object" && d) ? d.reason : null;
      setImportErr(_ga4ErrorTextForStatus(status, d?.reason_nl));
    } finally {
      setImporting(false);
    }
  };

  const askPlatformFix = async () => {
    setImporting(true); setImportErr(null);
    try {
      await api.post("/auth/google/services/platform-action", {
        service: "ga4",
        reason_raw: svc?.last_reason_raw
          || report?.last_error_reason_raw || null,
      });
      setImportErr(null);
      // Toon feedback via importErr-slot (dezelfde regel):
      setImportErr("Herstelverzoek genoteerd bij NEXORA.");
    } catch (_e) {
      setImportErr("Kon verzoek niet vastleggen. Probeer later opnieuw.");
    } finally {
      setImporting(false);
    }
  };

  const disabled = connector.permission === "PRIVATE";
  const isVerified = svc?.state === "verified";
  const stateLabelNL = svc?.last_status_nl
    || svc?.state_nl
    || (svc ? "Wordt opgehaald" : "Nog niet gekoppeld");
  const t = report?.totals || null;
  const fmtNum = (n) => Number(n || 0).toLocaleString("nl-NL");
  const fmtPct = (n) =>
    n == null ? "onbekend" : `${Math.round(Number(n) * 100)}%`;
  const fmtWhen = (iso) => formatFetchedAt(iso, report?.time_zone);

  return (
    <div id="google-analytics"
         data-testid="google-analytics-box"
         className="mb-4 p-3 rounded-md border border-border-hairline bg-bg-panel space-y-2 scroll-mt-24">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-text-primary font-semibold">
          Google Analytics
        </div>
        <div className="text-[10px]">
          {isVerified
            ? <span className="text-pos-green font-semibold"
                     data-testid="google-analytics-verified-badge">Verbonden</span>
            : <span className="text-text-subtle"
                     data-testid="google-analytics-not-verified-badge">Niet verbonden</span>}
        </div>
      </div>

      <div className="text-[11px] text-text-muted leading-relaxed"
           data-testid="google-analytics-state-nl">
        {isVerified
          ? `Verbonden met ${svc?.account_name || "Google Analytics"}`
          : stateLabelNL}
      </div>
      <SchedulerStatusLine svc={svc} testid="google-analytics-scheduler-line" />

      <div className="text-[11px] text-text-muted leading-relaxed">
        NEXORA leest websitebezoeken en gemeten belangrijke acties. Geen bewezen klanten.
      </div>

      {isVerified && t && (
        <div data-testid="google-analytics-performance"
             className="rounded border border-border-hairline bg-bg-panel-raised p-3 space-y-2">
          <div className="text-[10px] text-text-muted">
            Prestaties {formatPeriodNL(report.period_start, report.period_end)}
          </div>
          {report.time_zone && (
            <div className="text-[9px] text-text-subtle -mt-1">
              tijdzone: {report.time_zone}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Kpi label="Sessies"
                 value={fmtNum(t.sessions)}
                 testid="ga4-kpi-sessions" />
            <Kpi label="Actieve gebruikers"
                 value={fmtNum(t.active_users)}
                 testid="ga4-kpi-active-users" />
            <Kpi label="Betrokkenheid"
                 value={fmtPct(t.avg_engagement_rate)}
                 testid="ga4-kpi-engagement" />
            <Kpi label="Gemeten conversies"
                 value={fmtNum(t.conversions)}
                 sub="gemeten door Google Analytics, niet gelijk aan klanten"
                 testid="ga4-kpi-conversions" />
          </div>
          {report.per_channel && report.per_channel.length > 0 && (
            <div className="pt-2 border-t border-border-hairline"
                 data-testid="ga4-top-channels">
              <div className="text-[9px] text-text-muted mb-1">
                Belangrijkste kanalen
              </div>
              <ul className="space-y-0.5">
                {report.per_channel.slice(0, 5).map((ch) => (
                  <li key={ch.channel}
                      className="text-[11px] text-text-primary/85 flex justify-between gap-3">
                    <span>{ch.channel}</span>
                    <span className="text-text-muted">
                      {fmtNum(ch.sessions)} sessies · {fmtNum(ch.conversions)} conv
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {report.is_empty_success && (
            <div className="text-[11px] text-text-muted"
                 data-testid="ga4-empty-period">
              Geen analytics-gegevens in deze periode.
            </div>
          )}
          {report.last_error_status && (
            <div className="text-[11px] text-warn-amber"
                 data-testid="ga4-last-error">
              Laatste update mislukt op {fmtWhen(report.last_error_at)} —
              {" "}{report.last_error_reason_nl}
            </div>
          )}
          <div className="text-[10px] text-text-subtle">
            Laatst bijgewerkt op {fmtWhen(report.fetched_at)}
          </div>
        </div>
      )}
      {isVerified && !t && !report?.last_error_status && (
        <div className="text-[11px] text-text-muted"
             data-testid="google-analytics-no-data-yet">
          Nog geen gegevens opgehaald.
        </div>
      )}
      {isVerified && report?.last_error_status && !t && (
        <div className="text-[11px] text-warn-amber leading-snug"
             data-testid="ga4-error-message">
          {report.last_error_reason_nl
            || "Ophalen is nu niet mogelijk."}
        </div>
      )}

      {err && (
        <div className="text-[11px] text-neg-red">{err}</div>
      )}
      {importErr && (
        <div className="text-[11px] text-neg-red"
             data-testid="ga4-import-error">{importErr}</div>
      )}

      {!disabled && isVerified && (
        <div className="flex flex-col gap-2">
          {/* Bij api_disabled / provider_blocked → M127-platform-action-flow */}
          {(report?.last_error_status === "api_disabled"
            || report?.last_error_status === "provider_blocked") && (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[10px] text-text-muted max-w-[280px] leading-snug"
                   data-testid="ga4-platform-action-hint">
                Meldt intern dat NEXORA deze dienst moet herstellen. Google
                wordt hier niet automatisch geactiveerd.
              </div>
              <button type="button"
                      onClick={askPlatformFix}
                      disabled={importing}
                      data-testid="ga4-platform-action"
                      className="text-[11px] text-accent-teal hover:underline disabled:opacity-60 whitespace-nowrap">
                NEXORA laten herstellen
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <button type="button"
                    onClick={doImport}
                    disabled={importing}
                    data-testid="google-analytics-refresh-btn"
                    className="h-8 px-3 rounded bg-accent-teal/15 hover:bg-accent-teal/25 text-accent-teal border border-accent-teal/40 text-xs font-semibold flex items-center gap-1 disabled:opacity-60">
              <Download className="w-3 h-3" />
              {importing ? "Bezig met ophalen…" : (t ? "Bijwerken" : "Gegevens ophalen")}
            </button>
          </div>
        </div>
      )}

      {disabled && (
        <div className="text-[11px] text-text-muted">
          Zet permissie op LEZEN of hoger om te kunnen koppelen.
        </div>
      )}
    </div>
  );
}
